import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Label } from 'ng2-charts';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '~environments/environment';

export interface Region {
  id: number;
  name: string;
}

export interface Frac {
  id: number;
  name: string;
}

export interface Carrier {
  id: number;
  name: string;
}

export interface KPIDashboardStatsResponse {
  ocrVerifiedLoads: Stats;
  pendingLoads: Stats;
  inProgressLoads: Stats;
  completedLoads: Stats;
  revenue: Stats;
}

export interface Stats {
  trend: string;
  value: number;
  difference: number;
}

export interface GraphDataResponse {
  labels: Label[];
  datasets: GraphData[];
}

export interface GraphData {
  data: number[];
  label: string;
}

export interface FracStatRow {
  fracID: number;
  fracName: string;
  waitTime: string;
  currentStage: number;
  totalStages: number;
  trucksOnLocation: number;
  loadsInProgress: number;
  plannedPumpAmount: number;
  actualPumpedAmount: number;
  percentageDifference: number;
}

export interface CarrierStatRow {
  carrierName: string;
  averageDispatchTime: string;
  missedLoads: string;
  averageCompletionTime: string;
  uniqueDriverCount: string;
}

@Injectable({
  providedIn: 'root',
})
export class KPIDashboardService {
  private regionIDSubject$ = new BehaviorSubject<number>(0);
  public regionID$ = this.regionIDSubject$.asObservable();
  private fracsIDSubject$ = new BehaviorSubject<number[]>([]);
  public fracsID$ = this.fracsIDSubject$.asObservable();
  private carrierIDSubject$ = new BehaviorSubject<number>(0);
  public carrierID$ = this.carrierIDSubject$.asObservable();
  private timeFrameSubject$ = new BehaviorSubject<number>(0);
  public timeFrame$ = this.timeFrameSubject$.asObservable();
  private medianSubject$ = new BehaviorSubject<number>(0);
  public median$ = this.medianSubject$.asObservable();

  constructor(private http: HttpClient, private route: ActivatedRoute) {
    this.route.queryParamMap.pipe(map((params) => Number(params.get('region_id') || 0))).subscribe((regionID) => {
      this.regionIDSubject$.next(regionID);
    });
  }

  public setRegionID(regionID: number): void {
    this.regionIDSubject$.next(regionID);
  }

  public setTimeframe(timeframe: number): void {
    this.timeFrameSubject$.next(timeframe);
  }

  public setMedian(median: number): void {
    this.medianSubject$.next(median);
  }

  public getRegions$(): Observable<Region[]> {
    return this.http.get<Region[]>(`${environment.api}/dashboard/regions`);
  }

  public setFracsID(fracsID: number[]): void {
    this.fracsIDSubject$.next(fracsID);
  }

  public getFracs$(regionID: number): Observable<Frac[]> {
    return this.http.get<Frac[]>(`${environment.api}/dashboard/fracs`, {
      params: {
        regionID: `${regionID}`,
      },
    });
  }

  public setCarrierID(carrierID: number): void {
    this.carrierIDSubject$.next(carrierID);
  }

  public getCarriers$(): Observable<Carrier[]> {
    return this.http.get<Carrier[]>(`${environment.api}/dashboard/carriers`);
  }

  public getCarriersByRegionID$(regionID: number): Observable<Carrier[]> {
    return this.http.get<Carrier[]>(`${environment.api}/dashboard/carriers`, {
      params: {
        regionID: `${regionID}`,
      },
    });
  }

  public getStats$(regionID: number, fracsID: number[]): Observable<KPIDashboardStatsResponse> {
    return this.http.get<KPIDashboardStatsResponse>(`${environment.api}/dashboard/stats`, {
      params: {
        fracsID: `${fracsID}`,
        regionID: `${regionID}`,
      },
    });
  }

  public getTimeToNPT$(regionID: number, fracsID: number[], timeframe: number): Observable<GraphDataResponse> {
    return this.http.get<GraphDataResponse>(`${environment.api}/dashboard/timeToNPT`, {
      params: {
        fracsID: `${fracsID}`,
        interval: timeframe + ' days',
        regionID: `${regionID}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  }

  public getDriverWithCompletedLoads$(
    regionID: number,
    fracsID: number[],
    timeframe: number,
  ): Observable<GraphDataResponse> {
    return this.http.get<GraphDataResponse>(`${environment.api}/dashboard/driverCompletedLoads`, {
      params: {
        fracsID: `${fracsID}`,
        interval: timeframe + ' days',
        regionID: `${regionID}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  }

  public getAverageTurnRate$(
    regionID: number,
    fracsID: number[],
    carrierID: number,
    timeframe: number,
  ): Observable<GraphDataResponse> {
    return this.http.get<GraphDataResponse>(`${environment.api}/dashboard/averageTurnRate`, {
      params: {
        carrierID: `${carrierID}`,
        fracsID: `${fracsID}`,
        interval: timeframe + ' days',
        regionID: `${regionID}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  }

  public getPendingLoadByHour$(regionID: number, fracsID: number[], timeframe: number): Observable<GraphDataResponse> {
    return this.http.get<GraphDataResponse>(`${environment.api}/dashboard/pendingLoadsByHour`, {
      params: {
        fracsID: `${fracsID}`,
        interval: timeframe + ' days',
        regionID: `${regionID}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  }

  public getFracsStats$(regionID: number, fracsID: number[]): Observable<FracStatRow[]> {
    return this.http.get<FracStatRow[]>(`${environment.api}/dashboard/fracs_stats`, {
      params: {
        fracsID: `${fracsID}`,
        regionID: `${regionID}`,
      },
    });
  }

  public getCarriersStats$(
    regionID: number,
    median: number,
    fracsID: number[],
    timeframe: number,
  ): Observable<CarrierStatRow[]> {
    return this.http.get<CarrierStatRow[]>(`${environment.api}/dashboard/carriers_stats`, {
      params: {
        fracsID: `${fracsID}`,
        median: `${median}`,
        regionID: `${regionID}`,
        timeframe: `${timeframe}`,
      },
    });
  }

  public getPumpVolumePercentileStats$(
    regionID: number,
    fracsID: number[],
    timeframe: number,
    percentile: number,
  ): Observable<GraphDataResponse> {
    return this.http.get<GraphDataResponse>(`${environment.api}/dashboard/pump_volume_stats`, {
      params: {
        fracID: `${fracsID}`,
        percentile: `${percentile}`,
        regionID: `${regionID}`,
        timeframe: `${timeframe}`,
      },
    });
  }

  public getLocalStorage(key: string): any {
    const localFilter = JSON.parse(localStorage.getItem('KPI_DASHBOARD_FILTERS'));
    if (localFilter == null) {
      return null;
    }

    if (key === '') {
      return localFilter;
    }

    return localFilter[key];
  }

  public setLocalStorage(key: string, value: any): void {
    if (key === '') {
      return;
    }
    const localFilter: Record<string, any> = JSON.parse(localStorage.getItem('KPI_DASHBOARD_FILTERS') || '{}');
    localFilter[key] = value;
    localStorage.setItem('KPI_DASHBOARD_FILTERS', JSON.stringify(localFilter));
  }
}
